import { type JSX, useEffect, useState } from "react";
import { useGetSavedCredentials } from "@via/pages/account/profile/profile.api.tsx";
import {
  CredentialView,
  type GetUserCredentialsResponse,
  type ProfileClusterForClient,
} from "common";
import { useForm } from "react-hook-form";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import {
  datasourceOptions,
  sshDataConnectionOptions,
} from "@via/utils/form-validators.tsx";
import {
  InformationCircleIcon,
  PlusCircleIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import { ROUTES } from "@via/utils/constants.tsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  getDirForSsh,
  useSSHFolderList,
} from "@via/pages/data/data-sources/data-source.api.tsx";
import { useSshDataConnection } from "@via/hooks/useSshDataConnection.tsx";
import RunEnvironmentForm from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-form.tsx";
import Drawer from "@via/components/drawer.tsx";
import { useUserConfigurations } from "@via/api/configuration.tsx";

interface SshDataConnectionCredentials {
  runEnvId: number;
  dir: string;
}

/**
 * Which run environments this page may offer as the source of an SSH data
 * connection. Exported for its own test: mounting the whole connector to reach
 * it would drag in the router, react-select and the run environment drawer,
 * and none of that is what the rule is about.
 *
 * Agent environments are offered only when the deployment turned
 * AGENT_RUNS_ENABLED on (spec 2026-09-28, section 4.4). With it off this is
 * exactly the old rule, so the feature ships dark.
 */
export const getDisplayableRunEnvironments = (
  savedCredentials: GetUserCredentialsResponse | undefined,
  agentRunsEnabled: boolean
) => {
  return (
    savedCredentials?.profiles.filter(profile => {
      // ⚠️ An agent environment has a null sshId, so no ssh row matches it and
      // the hide check below would read !undefined?.hide, which is true. The
      // transport decides for agents, never the hide check.
      if ("agent" === profile.transport) return agentRunsEnabled;
      const sshObject = savedCredentials.ssh.find(
        ssh => ssh.id === profile.sshId
      );
      return !sshObject?.hide;
    }) || []
  );
};

export const runEnvironmentOptionLabel = (profile: {
  name: string;
  transport?: string | null;
}): string =>
  "agent" === profile.transport ? `${profile.name} (Agent)` : profile.name;

export default function SshDataConnector(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const [runEnvironmentFormKey, setRunEnvironmentFormKey] = useState(1);
  const [selectedPublicTemplate, setSelectedPublicTemplate] =
    useState<ProfileClusterForClient>();
  const { data: savedCredentials, isLoading: isSavedCredentialsLoading } =
    useGetSavedCredentials({ type: CredentialView.UserOwned });
  const { data: userConfigurations } = useUserConfigurations();
  const runEnvironments = getDisplayableRunEnvironments(
    savedCredentials,
    Boolean(userConfigurations?.AGENT_RUNS_ENABLED)
  );

  useEffect(() => {
    setRunEnvironmentFormKey(prev => prev + 1);
    setSelectedPublicTemplate(undefined);
  }, [isDrawerOpen]);

  const {
    data: publicSavedCredentials,
    isLoading: isPublicSavedCredentialsLoading,
  } = useGetSavedCredentials({ type: CredentialView.Public });
  const publicRunEnvironments = publicSavedCredentials?.profiles || [];

  const { mutateAsync: getFolderList, isPending: isGetFolderListLoading } =
    useSSHFolderList();

  const isLoading =
    isSavedCredentialsLoading ||
    isGetFolderListLoading ||
    isPublicSavedCredentialsLoading;

  const { setConnectionCredentials } = useSshDataConnection();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SshDataConnectionCredentials>({
    mode: "onChange",
    defaultValues: {
      dir: "/",
    },
  });
  const savedRunEnvIdValue = watch("runEnvId");
  useEffect(() => {
    if (savedRunEnvIdValue === 0) {
      setIsDrawerOpen(true);
    }
  }, [savedRunEnvIdValue]);

  const handleSshDataConnectionFormSubmit = async (
    formPayload: SshDataConnectionCredentials
  ) => {
    const folderListPayload = {
      savedCredentialId: formPayload.runEnvId,
      bucketName: formPayload.dir,
    };
    await getFolderList(folderListPayload);
    setConnectionCredentials({
      savedCredentialId: folderListPayload.savedCredentialId,
      bucketName: getDirForSsh(folderListPayload),
    });

    const datasetId = searchParams.get("datasetId");
    const url = datasetId
      ? `${ROUTES.DATA_SOURCE_PROVIDER_SSH}?datasetId=${datasetId}`
      : `${ROUTES.DATA_SOURCE_PROVIDER_SSH}`;
    navigate(url);
  };

  const handleCancel = () => {
    navigate(ROUTES.DATA);
  };

  const handleRunEnvCreate = (runEnvId: number) => {
    if (runEnvId) {
      setValue("runEnvId", runEnvId);
    }
  };

  return (
    <div>
      {isSavedCredentialsLoading ? (
        <SkeletonLoader count={1} />
      ) : (
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(handleSshDataConnectionFormSubmit)(e);
          }}>
          <div className="max-w-[850px] mx-auto space-y-6 pb-5 pt-3">
            <div className="max-w-[850px] space-y-6 pb-5 pt-6">
              <div>
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Choose Run Environment&nbsp;
                  <span className="text-red-400">*</span>
                </label>
                <div className="mt-2" data-testid="runEnvironmentsSelector">
                  <SelectComponent
                    control={control}
                    name="runEnvId"
                    rules={sshDataConnectionOptions.runEnvId}
                    formatOptionLabel={option => {
                      return option.value === 0 ? (
                        <div className="flex flex-row items-center font-semibold">
                          <PlusCircleIcon className="h-5 w-5 mr-2" />{" "}
                          {option.label}
                        </div>
                      ) : (
                        option.label
                      );
                    }}
                    options={[
                      { label: "New run environment", value: 0 },
                      ...(runEnvironments?.map(profile => ({
                        label: runEnvironmentOptionLabel(profile),
                        value: profile.id,
                      })) ?? []),
                    ]}
                    isLoading={isSavedCredentialsLoading}
                    placeholder="Choose Run Environment"
                  />
                  {errors.runEnvId && (
                    <span className="text-xs text-rose-500">
                      {errors.runEnvId.message}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
                  Data Source Path&nbsp;<span className="text-red-400">*</span>
                  <InformationCircleIcon
                    id={"sshPathTooltipIcon"}
                    className="mx-2 h-4"
                  />
                  <Tooltip
                    anchorSelect={"#sshPathTooltipIcon"}
                    content={"The existing ssh data source path to access"}
                  />
                </label>
                <div className="mt-2">
                  <input
                    data-testid="sshDataPathInput"
                    {...register("dir", datasourceOptions.path)}
                    type="text"
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    placeholder="Enter Your Data Source Path"
                  />
                  {errors.dir && (
                    <span className="text-xs text-rose-500">
                      {errors.dir.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 justify-end py-4">
              <button
                type="button"
                data-testid="cloudConnectorCancelButton"
                onClick={handleCancel}
                disabled={isLoading}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Cancel
              </button>
              <button
                data-testid="cloudConnectorSubmitButton"
                type="submit"
                disabled={isLoading}
                className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <Spinner loading={isLoading}>Connect</Spinner>
              </button>
            </div>
          </div>
        </form>
      )}
      <Drawer
        id="run-environments"
        open={isDrawerOpen}
        setOpen={setIsDrawerOpen}>
        <RunEnvironmentForm
          setOpen={setIsDrawerOpen}
          publicRunEnvironments={publicRunEnvironments}
          editableRunEnvironment={selectedPublicTemplate}
          setEditableRunEnvironment={setSelectedPublicTemplate}
          key={runEnvironmentFormKey}
          // The drawer creates SSH environments only, because an agent
          // environment needs an installed agent before it can be browsed.
          sshOnly
          onSuccess={handleRunEnvCreate}
        />
      </Drawer>
    </div>
  );
}
